import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ChevronLeft, ChevronRight, Phone, Users, Clock, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function fmtDate(d) { return d.toISOString().split('T')[0]; }

function getWeekDays(ws) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(ws);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function fmtTime12(t) {
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  return `${hr === 0 ? 12 : hr > 12 ? hr - 12 : hr}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
}

const InterviewCalendarPage = () => {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekDays = getWeekDays(weekStart);

  const fetchInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/interview-scheduling/upcoming-interviews`, {
        params: { week_start: fmtDate(weekStart) }
      });
      setInterviews(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { fetchInterviews(); }, [fetchInterviews]);

  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const goToday = () => setWeekStart(getWeekStart(new Date()));

  const weekLabel = `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  // Group interviews by date
  const byDate = {};
  interviews.forEach(iv => {
    if (!byDate[iv.date]) byDate[iv.date] = [];
    byDate[iv.date].push(iv);
  });

  const todayStr = fmtDate(new Date());
  const totalThisWeek = interviews.length;
  const phoneCount = interviews.filter(i => i.interview_type === 'phone').length;
  const inPersonCount = interviews.filter(i => i.interview_type === 'in_person').length;

  return (
    <div className="space-y-6" data-testid="interview-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#4B2D6E] font-[Lato]">Interview Calendar</h1>
          <p className="text-sm text-[#666]">All upcoming interviews at a glance</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Badge variant="outline" className="border-violet-300 text-violet-700 gap-1.5 py-1">
            <Phone className="h-3.5 w-3.5" /> {phoneCount} Phone
          </Badge>
          <Badge variant="outline" className="border-amber-300 text-amber-700 gap-1.5 py-1">
            <Users className="h-3.5 w-3.5" /> {inPersonCount} In-Person
          </Badge>
          <Badge variant="outline" className="text-[#4B2D6E] gap-1.5 py-1">
            {totalThisWeek} Total
          </Badge>
        </div>
      </div>

      {/* Week Navigation */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={prevWeek} data-testid="cal-prev-week">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <CalendarDays className="h-5 w-5 text-[#4B2D6E]" />
              <h2 className="font-semibold text-[#4B2D6E] text-lg">{weekLabel}</h2>
              <Button variant="outline" size="sm" onClick={goToday} className="text-xs" data-testid="cal-today-btn">Today</Button>
            </div>
            <Button variant="ghost" size="icon" onClick={nextWeek} data-testid="cal-next-week">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#4B2D6E] border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {weekDays.map((day, i) => {
            const dateStr = fmtDate(day);
            const isToday = dateStr === todayStr;
            const dayInterviews = byDate[dateStr] || [];

            return (
              <div
                key={i}
                className={`rounded-xl border ${isToday ? 'border-[#E8722A] bg-[#E8722A]/[0.03] ring-1 ring-[#E8722A]/20' : 'border-gray-200 bg-white'} min-h-[180px]`}
                data-testid={`cal-day-${dateStr}`}
              >
                {/* Day Header */}
                <div className={`px-3 py-2.5 border-b ${isToday ? 'border-[#E8722A]/20' : 'border-gray-100'}`}>
                  <p className={`text-xs font-semibold ${isToday ? 'text-[#E8722A]' : 'text-[#999]'}`}>
                    {DAY_SHORT[i]}
                  </p>
                  <p className={`text-lg font-bold ${isToday ? 'text-[#E8722A]' : 'text-[#333]'}`}>
                    {day.getDate()}
                  </p>
                </div>

                {/* Interviews */}
                <div className="p-2 space-y-2">
                  {dayInterviews.length === 0 ? (
                    <p className="text-xs text-gray-300 text-center py-4">No interviews</p>
                  ) : (
                    dayInterviews.map(iv => (
                      <button
                        key={iv.id}
                        onClick={() => navigate(`/dashboard/candidates/${iv.candidate_id}`)}
                        className={`w-full text-left rounded-lg p-2.5 transition-all hover:ring-2 ${
                          iv.interview_type === 'phone'
                            ? 'bg-violet-50 hover:ring-violet-300'
                            : 'bg-amber-50 hover:ring-amber-300'
                        }`}
                        data-testid={`cal-interview-${iv.id}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          {iv.interview_type === 'phone'
                            ? <Phone className="h-3 w-3 text-violet-600" />
                            : <Users className="h-3 w-3 text-amber-600" />
                          }
                          <span className={`text-[10px] font-semibold uppercase tracking-wide ${
                            iv.interview_type === 'phone' ? 'text-violet-600' : 'text-amber-600'
                          }`}>
                            {iv.interview_type === 'phone' ? 'Phone' : 'In-Person'}
                          </span>
                        </div>
                        <p className="font-semibold text-[#333] text-sm truncate">{iv.candidate_name}</p>
                        <p className="text-[10px] text-[#666] truncate">{iv.job_title}</p>
                        <div className="flex items-center gap-1 mt-1.5 text-[10px] text-[#999]">
                          <Clock className="h-2.5 w-2.5" />
                          <span>{fmtTime12(iv.start_time)}</span>
                          <span>· {iv.duration_minutes}m</span>
                        </div>
                        {iv.admin_name && (
                          <p className="text-[10px] text-[#bbb] mt-0.5 truncate">w/ {iv.admin_name}</p>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && interviews.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <CalendarDays className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-[#666] font-medium">No interviews scheduled this week</p>
            <p className="text-sm text-[#999] mt-1">Advance candidates through the pipeline to see interviews here.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InterviewCalendarPage;
